import { Link } from 'react-router-dom';
import VerticalComparisonMatrix from '../../components/VerticalComparisonMatrix';

function Section({ title, bullets }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-[var(--gradient-card)] p-7 backdrop-blur-xl">
      <div className="text-lg font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
        {title}
      </div>
      <ul className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
        {bullets.map((b) => (
          <li key={b} className="leading-6">
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Infrastructure() {
  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[var(--gradient-hero)] p-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_30%,rgba(5,150,105,0.12),transparent_55%),radial-gradient(circle_at_70%_25%,rgba(37,99,235,0.16),transparent_50%)]" />
        <div className="relative">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Industries</div>
          <h1
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Digital & Critical Infrastructure
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            Critical infrastructure sectors including energy, utilities, telecommunications, transportation, and
            healthcare require specialized identity controls that balance operational technology security with IT
            systems while ensuring continuity of essential services.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20"
            >
              Request Infrastructure Assessment
            </Link>
            <Link
              to="/architecture"
              className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] px-5 py-3 text-sm font-semibold text-[var(--text-primary)] hover:bg-white/[0.05]"
            >
              View Reference Architecture
            </Link>
          </div>
        </div>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          OT/ICS Identity Management
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="SCADA System Access"
            bullets={["Role-based access for Supervisory Control and Data Acquisition systems with engineering workstation controls"]}
          />
          <Section
            title="DCS Operator Identity"
            bullets={["Shift-based operator authentication for Distributed Control Systems with handover documentation"]}
          />
          <Section
            title="PLC/RTU Access Management"
            bullets={["Controlled access to Programmable Logic Controllers and Remote Terminal Units with change management integration"]}
          />
          <Section
            title="HMI Authentication"
            bullets={["Operator interface authentication with quick switch capabilities for emergency situations"]}
          />
          <Section
            title="IT/OT Boundary Controls"
            bullets={["Identity-based segmentation between corporate IT and operational technology networks"]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Field Operations & Remote Access
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Field Technician Identity"
            bullets={["Mobile-first identity for field workers accessing substations, cell towers, and remote facilities"]}
          />
          <Section
            title="Contractor Site Access"
            bullets={["Time-bound, location-restricted access for third-party maintenance crews and construction workers"]}
          />
          <Section
            title="Remote Substation Access"
            bullets={["Secure remote access for unmanned facilities with video verification and local override capabilities"]}
          />
          <Section
            title="Vendor Remote Support"
            bullets={["Controlled vendor access for equipment diagnostics with session recording and approval workflows"]}
          />
          <Section
            title="Emergency Response Identity"
            bullets={["Expedited identity provisioning for disaster response and mutual aid personnel"]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Consumer & Citizen Identity
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Smart Meter Identity"
            bullets={["Device identity for AMI (Advanced Metering Infrastructure) with secure communication to head-end systems"]}
          />
          <Section
            title="Consumer Portal SSO"
            bullets={["Unified Customer identity for bill payment, usage monitoring, and service requests across utilities"]}
          />
          <Section
            title="EV Charging Identity"
            bullets={["Driver authentication for electric vehicle charging networks with roaming and payment integration"]}
          />
          <Section
            title="Patient Identity (Healthcare)"
            bullets={["Unified patient identity across hospitals, clinics, and pharmacies with consent management"]}
          />
          <Section
            title="Telecom Subscriber Identity"
            bullets={["SIM-based and app-based identity for mobile subscribers with eSIM provisioning support"]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Critical Systems Privileged Access
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="Grid Control Center PAM"
            bullets={["Privileged access for energy management systems controlling power generation and distribution"]}
          />
          <Section
            title="Network Operations Center"
            bullets={["Tiered access for telecom NOC with escalation paths and vendor access controls"]}
          />
          <Section
            title="Healthcare System PAM"
            bullets={["Privileged access for HIS, LIS, and RIS systems containing protected health information"]}
          />
          <Section
            title="Pipeline SCADA Access"
            bullets={["Multi-factor, location-verified access for oil/gas pipeline control systems"]}
          />
          <Section
            title="Safety System Access"
            bullets={["Strict controls for Safety Instrumented Systems (SIS) with engineering change management"]}
          />
        </div>
      </div>

      <div className="mt-14 rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-xl sm:p-10">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Sector-Specific Compliance
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="CEA Cybersecurity Guidelines"
            bullets={["Identity controls meeting Central Electricity Authority requirements for power sector"]}
          />
          <Section
            title="TRAI Security Framework"
            bullets={["Telecom Regulatory Authority of India mandated security controls for telecom operators"]}
          />
          <Section
            title="NCIIPC Guidelines"
            bullets={["Critical information infrastructure protection requirements from NCIIPC"]}
          />
          <Section
            title="HIPAA/DISHA Compliance"
            bullets={["Healthcare data protection identity controls meeting DISHA expectations"]}
          />
          <Section
            title="NERC CIP (Global)"
            bullets={["Critical infrastructure protection standards for utilities with international operations"]}
          />
        </div>
      </div>

      <div className="mt-14">
        <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
          Infrastructure Security
        </h2>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Section
            title="ICS Threat Detection"
            bullets={["Identity-based anomaly detection for industrial control system access patterns"]}
          />
          <Section
            title="Supply Chain Identity"
            bullets={["Verification of firmware/software sources and vendor authenticity for critical systems"]}
          />
          <Section
            title="Physical-Cyber Convergence"
            bullets={["Unified identity for physical access badges and logical system access at critical facilities"]}
          />
          <Section
            title="Resilience & Continuity"
            bullets={["Offline-capable authentication for continued operations during network outages or attacks"]}
          />
        </div>
      </div>

      <VerticalComparisonMatrix />
    </div>
  );
}
